import React, { useState } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Location } from 'react-router-dom';
import {
  FileText,
  Users,
  BuildingOffice,
  Lightning,
  MagnifyingGlass,
  SpeakerLow,
  CheckSquare,
  Folders,
  PlusCircle,
  Microphone,
  Files,
  Question,
  ShieldCheck
} from '@phosphor-icons/react';
import { NavItem } from '@/types';
import { useAppUser } from '@/contexts/AppUserProvider';
import { SearchPopover } from '@/components/SearchPopover';

interface NavigationItemsProps {
  navItems: NavItem[];
  location: Location;
  isCollapsed?: boolean;
  onNavigate?: () => void; // Callback when navigation occurs (for closing mobile drawer)
}

// Icon mapping for dynamic icon rendering
const iconMap: Record<string, React.ReactNode> = {
  Organization: <BuildingOffice size={20} />,
  CheckSquare: <CheckSquare size={20} />,
  FileText: <FileText size={20} />,
  Users: <Users size={20} />,
  FoldersIcon: <Folders size={20} />,
  SpeakerLow: <SpeakerLow size={20} />,
  Files: <Files size={20} />,
  PlusCircle: <PlusCircle size={20} />,
  Psychology: <Lightning size={20} weight="light" />,
  MagnifyingGlass: <MagnifyingGlass size={20} weight="light" />,
  Mic: <Microphone size={20} />,
  Question: <Question size={20} />,
  ShieldCheck: <ShieldCheck size={20} />
};

export const NavigationItems: React.FC<NavigationItemsProps> = ({
  navItems,
  location,
  isCollapsed = false,
  onNavigate
}) => {
  const { appUser, selectedUserRole } = useAppUser();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchAnchorEl, setSearchAnchorEl] = useState<HTMLElement | null>(
    null
  );

  // Ensure any open search popover closes when switching between mobile/desktop
  React.useEffect(() => {
    setSearchAnchorEl(null);
  }, [isMobile]);

  if (!appUser || !selectedUserRole) {
    return null;
  }

  const handleSearchClick = (event: React.MouseEvent<HTMLElement>) => {
    setSearchAnchorEl(event.currentTarget); // We still need this for the open state
  };

  const handleSearchClose = () => {
    setSearchAnchorEl(null);
  };

  const handleSearchNavigate = (path: string) => {
    navigate(path);
    // Close drawer on mobile after navigation from search
    if (isMobile && onNavigate) {
      onNavigate();
    }
  };

  return (
    <>
      <Box sx={{ px: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {navItems.map(({ label, icon, path, isAction }) => {
          const isActive = !isAction && location.pathname === path;
          return (
            <Box
              key={label}
              data-onboarding-target={!isAction ? path : undefined}
              onClick={isAction ? handleSearchClick : (e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Navigating to:', path);
                navigate(path);
                // Close drawer on mobile after navigation
                if (isMobile && onNavigate) {
                  onNavigate();
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                px: isCollapsed ? 2 : 3,
                py: 1.5,
                borderRadius: '10px',
                color: isActive
                  ? theme.palette.text.primary
                  : theme.palette.text.secondary,
                backgroundColor: isActive
                  ? theme.palette.action.selected
                  : 'transparent',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: isActive ? 600 : 500,
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                pointerEvents: 'auto', // Ensure clicks work
                position: 'relative', // Ensure proper stacking
                zIndex: 1, // Above any potential overlays
                transition: 'all 0.15s ease',
                '&:hover': {
                  backgroundColor: isActive
                    ? theme.palette.action.selected
                    : theme.palette.action.hover,
                  color: theme.palette.text.primary
                }
              }}
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  opacity: isActive ? 1 : 0.7
                }}
              >
                {iconMap[icon] || <Box sx={{ width: 20, height: 20 }} />}
              </Box>
              {!isCollapsed && (
                <Box sx={{ fontSize: '14px', fontWeight: 500 }}>{label}</Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Search Popover */}
      <SearchPopover
        open={Boolean(searchAnchorEl)}
        onClose={handleSearchClose}
        onNavigate={handleSearchNavigate}
      />
    </>
  );
};
